<script>

  import { External, M } from '../external';
  import { fade } from 'svelte/transition';
  import { makeWebsiteRequest } from '../Common/tools';
  import PaymentProcessor from './PaymentProcessors/PaymentProcessor.svelte';
  import Loader from '../Common/Loader.svelte';
  import { createEventDispatcher, onMount } from 'svelte';

  export let currentPlan, vatRates, paymentProcessor, amount = 0, futureAmount = 0, currency = '', inCheckout = false;

  export const onAmountChange = () => {
    if (processorInstance) {
      processorInstance.onAmountChange();
    }
  };

  export const exportUpdatedBillingDetails = async () => {
    let data = {};

    if (editingCard) {
      data = {
        updatePaymentMethod: '1',
        ...(await processorInstance.getPaymentDetails(editingDetails ? newBillingDetails : currentPlan.billingInfo))
      };
    }

    if (editingDetails) {
      data = {
        ...data,
        updateInfo: '1',
        name: newBillingDetails.name,
        companyName: newBillingDetails.companyName,
        companyId: newBillingDetails.companyId,
        vatId: newBillingDetails.vatId,
        countryCode: newBillingDetails.countryCode,
        city: newBillingDetails.city,
        state: newBillingDetails.state,
        zip: newBillingDetails.zip,
        address: newBillingDetails.address
      };
    }
    return data;
  };

  export const completeRequiredPaymentActions = async () => {
    if (processorInstance) {
      return await processorInstance.completeRequiredPaymentActions();
    }
  };

  let dispatch = createEventDispatcher();
  let canCancelEdits = true;
  let editingDetails = false;
  let editingCard = false;
  let newBillingDetails = {};
  let hasVatIdField = false;
  let vatIdValid = false;
  let states = [];
  let saving = false;
  let processorInstance = null;

  let editDetails = () => {
    newBillingDetails = { ...currentPlan.billingInfo || {} };
    editingDetails = true;
    onCountrySelected(false);
  };

  let onCountrySelected = (notify) => {
    hasVatIdField = !External.isUndefined(vatRates[newBillingDetails.countryCode]);
    states = External.STATES_ISO[newBillingDetails.countryCode] || [];
    validateVatId(false);
    if (notify) {
      fireTaxChangeEvent();
    }
  };

  let fireTaxChangeEvent = () => {
    dispatch('tax-change', {
      vatId: newBillingDetails.vatId,
      activeVatPercent: vatRates[newBillingDetails.countryCode] || 0
    });
  };

  let validateVatId = (notify) => {
    let vat = newBillingDetails.vatId;
    vatIdValid = vat === '' || /[A-Za-z]{2}[0-9A-Za-z\+\*\.]{7,12}/.test(vat) || (newBillingDetails.countryCode === 'NO' && vat.trim().length === 9);
    if (notify && vatIdValid) {
      fireTaxChangeEvent();
    }
  };

  let saveChanges = async () => {
    if (saving || !editingCard && !editingDetails) {
      return;
    }

    saving = true;

    let data;
    try {
      data = await exportUpdatedBillingDetails();
    } catch (error) {
      saving = false;
      throw error;
    }
    let result = await makeWebsiteRequest({
      action: 'updateInvoiceBillingInfo',
      data: data
    });

    if (result.ok) {
      currentPlan.billingInfo = result.data.billingInfo;
      currentPlan.activeVatPercent = result.data.activeVatPercent;
      currentPlan.taxPercent = result.data.taxPercent;

      if (processorInstance && editingCard) {
        await processorInstance.completeRequiredSetupActions();
      }

      cancelEdits();
    }

    saving = false;
  };

  let cancelEdits = () => {
    editingDetails = false;
    if (processorInstance) {
      processorInstance?.cancelEdits();
    }
    editingCard = false;
  };

  onMount(() => {
    newBillingDetails.countryCode = newBillingDetails.countryCode || External.platformCountry;
    canCancelEdits = inCheckout ? !!currentPlan.billingInfo?.name : true; // default false on checkout with no billing details
    editingDetails = inCheckout ? !currentPlan.billingInfo?.name : false; // default true on checkout with no billing details
  });
</script>

{#if saving}
  <div class="col20 f-left mt-35" style="min-height: 150px">
    <Loader />
  </div>
{/if}
<section class="flex align-center flex-col mt-25" class:none={saving}>
  <div class="w800 mb-35">
    <div class="billing-form-head flex align-center space-between">
      <h3 class="f-left ta-left mb-15 roboto flex-none">{M.BILLING_INFO}</h3>
      <div class="right-section" class:none={editingDetails}>
        <div class="controls payment-info-controlls">
          <a class="upgrade middle-button gray" on:click={editDetails}>{M.EDIT_BILLING}</a>
        </div>
      </div>
    </div>
    <div class="payment-info-wrapper mt-10">
      {#if editingDetails}
        <div class="payment-info" style="margin-top:0;" in:fade>
          <input type="text" placeholder="{M.FULL_NAME}" bind:value={newBillingDetails.name}>
          <input type="text" placeholder="{M.COMPANY_NAME} ({M.OPTIONAL})" bind:value={newBillingDetails.companyName}>
          <input type="text" placeholder="{M.ID_NUMBER} ({M.OPTIONAL})" bind:value={newBillingDetails.companyId}
                 class:none={!newBillingDetails.companyName}>
          {#if hasVatIdField}
            <input type="text" placeholder="{M.VAT_ID} ({M.OPTIONAL})" bind:value={newBillingDetails.vatId} on:keyup={() => validateVatId(true)}
                   class:none={!newBillingDetails.companyName} class:invalid-field={!vatIdValid}>
          {/if}
          <div class="col20 f-left flex flex-row">
            <select class="flex-1" name="country" bind:value={newBillingDetails.countryCode}
                    on:change={() => onCountrySelected(true)}>
              {#each External.COUNTRY_CODES as countryCode, idx}
                <option value={countryCode}>{External.COUNTRIES[idx] || countryCode}</option>
              {/each}
            </select>

            {#if !states.length}
              <!-- the "style" here is a dumb hack with text indent to make the input the same width as the select above... -->
              <input class="flex-1" type="text" placeholder="{M.STATE}" name="state" data-recurly="state"
                     style="margin-left: 25px; padding: 0px; text-indent: 19px"
                     bind:value={newBillingDetails.state}>
            {:else}
              <select class="flex-1" name="state" data-recurly="state" bind:value={newBillingDetails.state} style="margin-left: 25px">
                <option value="">Select State</option>
                {#each states as state}
                  <option value={state.code}>{state.name}</option>
                {/each}
              </select>
            {/if}
          </div>
          <div class="col20 f-left flex flex-row">
            <input class="flex-1" type="text" placeholder="{M.CITY}" name="city" data-recurly="city"
                   bind:value={newBillingDetails.city}>
            <input class="flex-1" type="text" placeholder="{M.POST_CODE}" name="zip" data-recurly="postal_code" style="margin-left: 25px"
                   bind:value={newBillingDetails.zip}>
          </div>
          <input type="text" placeholder="{M.ADDRESS}" name="address" bind:value={newBillingDetails.address}>

          <PaymentProcessor bind:this={processorInstance} settings={paymentProcessor} billingInfo={currentPlan.billingInfo} {amount} {futureAmount}
                            {inCheckout}
                            {currency} on:edit={() => editingCard = true} />
        </div>
      {:else}
        <div data-name="billing-info-read-only" in:fade>
          {#if !currentPlan.billingInfo?.name}
            <div class="col20 f-left no-details">
              <span>{M.NO_BILLING_DETAILS}</span>
            </div>
          {:else}

            <label data-name="fullName">
              <span>{M.FULL_NAME}</span>
              <h3>{@html currentPlan.billingInfo.name || '-'}</h3>
            </label>
            {#if currentPlan.billingInfo.companyName}
              <label data-name="companyName">
                <span>{M.COMPANY_NAME}</span>
                <h3>{@html currentPlan.billingInfo.companyName}</h3>
              </label>
              <label data-name="companyId">
                <span>{M.ID_NUMBER}</span>
                <h3>{@html currentPlan.billingInfo.companyId || '-'}</h3>
              </label>
              {#if currentPlan.billingInfo.vatId}
                <label data-name="vatId">
                  <span>{M.VAT_ID}</span>
                  <h3>{@html currentPlan.billingInfo.vatId || '-'}</h3>
                </label>
              {/if}
            {/if}

            <label data-name="country">
              <span>{M.COUNTRY}</span>
              <h3>{External.COUNTRIES[External.COUNTRY_CODES.indexOf(currentPlan.billingInfo.countryCode)]}</h3>
            </label>
            <label data-name="city">
              <span>{M.CITY}</span>
              <h3>{currentPlan.billingInfo.city || '-'}</h3>
            </label>
            <label data-name="state">
              <span>{M.STATE}</span>
              <h3>{currentPlan.billingInfo.state || '-'}</h3>
            </label>
            <label data-name="zip">
              <span>{M.POST_CODE}</span>
              <h3>{currentPlan.billingInfo.zip || '-'}</h3>
            </label>
            <label data-name="address">
              <span>{M.ADDRESS}</span>
              <h3>{currentPlan.billingInfo.address || '-'}</h3>
            </label>

            <PaymentProcessor bind:this={processorInstance} settings={paymentProcessor} billingInfo={currentPlan.billingInfo} {amount} {futureAmount}
                              {inCheckout}
                              {currency} on:edit={() => editingCard = true} />
          {/if}
        </div>
      {/if}

      {#if (editingCard || editingDetails) && (!inCheckout || canCancelEdits)}
        <div class="col20 f-left mt-15" data-role="billing-info-buttons">
          <a class="middle-button gray f-left mt-30" class:disabled={saving} style="margin-right:5px;" on:click={cancelEdits}>{M.CANCEL}</a>
          {#if !inCheckout}
            <a class="middle-button blue f-left mt-30" class:disabled={saving} on:click={saveChanges}>
              {paymentProcessor.type === 'paystack' || paymentProcessor.type === 'payfast' ? M.CONNECT_NEW_CARD : M.SAVE_CHANGES}
            </a>
          {/if}
        </div>
      {/if}
    </div>
  </div>
</section>
